///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Low-precision ephemeris for the sky: Greenwich sidereal time, the sun
/// (Meeus ch. 25, ~0.01 degree) and the moon (Astronomical Almanac
/// low-precision series, ~0.3 degree, a bit over half the moon's own
/// diameter). All directions use the engine's axis convention, where
/// right ascension plays the role of longitude: +Z at RA 0h, +X at RA 6h,
/// +Y toward the north celestial pole. Rotating a celestial direction about
/// Y by -GMST gives the Earth-fixed (world) direction the scene renders in.
///

import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { c_jd_2000 } from "@stovokor/math/time"
import { vec3 } from "@stovokor/math/vector"

export const cAstronomicalUnitKm = 149597870.7
export const cSunRadiusKm = 695700
export const cMoonRadiusKm = 1737.4

export interface BodyPosition {
	/** Unit direction from the Earth's center in the celestial frame */
	direction: vec3
	/** Right ascension in radians, 0..2pi */
	ra: number
	/** Declination in radians */
	dec: number
	/** Geocentric distance in km */
	distanceKm: number
}

function wrapAngle(angle: number): number {
	const wrapped = angle % trig.two_pi
	return wrapped < 0 ? wrapped + trig.two_pi : wrapped
}

function sinDeg(deg: number): number {
	return Math.sin(deg * trig.rad)
}

function cosDeg(deg: number): number {
	return Math.cos(deg * trig.rad)
}

/** Julian centuries since J2000.0 */
function centuries(julian: number): number {
	return (julian - c_jd_2000) / 36525
}

/**
 * Unit vector for a right ascension / declination pair in the engine's
 * celestial frame (see the file comment)
 */
export function equatorialDirection(ra: number, dec: number): vec3 {
	const cosDec = Math.cos(dec)
	return new vec3(Math.sin(ra) * cosDec, Math.sin(dec), Math.cos(ra) * cosDec)
}

/** Right ascension (0..2pi) and declination of a celestial-frame direction */
export function directionToEquatorial(direction: vec3): { ra: number, dec: number } {
	return {
		ra: wrapAngle(Math.atan2(direction.x, direction.z)),
		dec: Math.asin(Math.max(-1, Math.min(1, direction.y)))
	}
}

/** Mean obliquity of the ecliptic in radians (Meeus 22.2, truncated) */
export function obliquity(julian: number): number {
	const T = centuries(julian)
	return (23.439291 - 0.0130042 * T - 1.64e-7 * T * T) * trig.rad
}

/**
 * Greenwich mean sidereal time in radians (Meeus 12.4). The input is UT,
 * which is what the scene clock carries; the ~minute of TT-UT offset is
 * far below the precision of the series here.
 */
export function greenwichSiderealTime(julian: number): number {
	const T = centuries(julian)
	const degrees = 280.46061837
		+ 360.98564736629 * (julian - c_jd_2000)
		+ 0.000387933 * T * T
		- T * T * T / 38710000
	return wrapAngle(degrees * trig.rad)
}

/**
 * Ecliptic longitude/latitude (radians) and distance to an equatorial
 * BodyPosition at the given obliquity
 */
function fromEcliptic(lon: number, lat: number, distanceKm: number, eps: number): BodyPosition {
	const cosLat = Math.cos(lat)
	const x = cosLat * Math.cos(lon)
	const y = cosLat * Math.sin(lon)
	const z = Math.sin(lat)
	const ye = y * Math.cos(eps) - z * Math.sin(eps)
	const ze = y * Math.sin(eps) + z * Math.cos(eps)
	const ra = wrapAngle(Math.atan2(ye, x))
	const dec = Math.asin(Math.max(-1, Math.min(1, ze)))
	return { direction: equatorialDirection(ra, dec), ra, dec, distanceKm }
}

/**
 * Apparent geocentric position of the sun (Meeus ch. 25): includes the
 * equation of center, aberration and the main nutation term, so the
 * sub-solar point (and with it the terminator) lands within ~0.01 degree
 */
export function sunPosition(julian: number): BodyPosition {
	const T = centuries(julian)
	const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T
	const M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T
	const e = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T
	const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * sinDeg(M)
		+ (0.019993 - 0.000101 * T) * sinDeg(2 * M)
		+ 0.000289 * sinDeg(3 * M)
	const trueLongitude = L0 + C
	const trueAnomaly = M + C
	const distanceAu = 1.000001018 * (1 - e * e) / (1 + e * cosDeg(trueAnomaly))

	// Apparent longitude: aberration plus the dominant nutation term, and
	// the matching correction to the obliquity
	const omega = 125.04 - 1934.136 * T
	const apparentLongitude = trueLongitude - 0.00569 - 0.00478 * sinDeg(omega)
	const eps = obliquity(julian) + 0.00256 * cosDeg(omega) * trig.rad

	return fromEcliptic(apparentLongitude * trig.rad, 0, distanceAu * cAstronomicalUnitKm, eps)
}

/**
 * Geocentric position of the moon from the Astronomical Almanac's
 * low-precision series (good to ~0.3 degree in direction and ~0.2% in
 * distance over 1900-2100)
 */
export function moonPosition(julian: number): BodyPosition {
	const T = centuries(julian)
	const lon = 218.32 + 481267.881 * T
		+ 6.29 * sinDeg(135.0 + 477198.87 * T)
		- 1.27 * sinDeg(259.3 - 413335.36 * T)
		+ 0.66 * sinDeg(235.7 + 890534.22 * T)
		+ 0.21 * sinDeg(269.9 + 954397.74 * T)
		- 0.19 * sinDeg(357.5 + 35999.05 * T)
		- 0.11 * sinDeg(186.5 + 966404.03 * T)
	const lat = 5.13 * sinDeg(93.3 + 483202.02 * T)
		+ 0.28 * sinDeg(228.2 + 960400.89 * T)
		- 0.28 * sinDeg(318.3 + 6003.15 * T)
		- 0.17 * sinDeg(217.6 - 407332.21 * T)
	const parallax = 0.9508
		+ 0.0518 * cosDeg(135.0 + 477198.87 * T)
		+ 0.0095 * cosDeg(259.3 - 413335.36 * T)
		+ 0.0078 * cosDeg(235.7 + 890534.22 * T)
		+ 0.0028 * cosDeg(269.9 + 954397.74 * T)
	const distanceKm = 6378.14 / sinDeg(parallax)

	return fromEcliptic(lon * trig.rad, lat * trig.rad, distanceKm, obliquity(julian))
}

/**
 * Direction of the north ecliptic pole in the celestial frame. The moon's
 * spin axis is within 1.5 degrees of it, which is plenty for orienting the
 * lunar texture on a disc a fraction of a degree across
 */
export function eclipticNorthPole(julian: number): vec3 {
	const eps = obliquity(julian)
	return new vec3(-Math.sin(eps), Math.cos(eps), 0)
}

/**
 * Rotation taking celestial-frame directions to Earth-fixed world
 * directions for the given sidereal time: a point at right ascension a
 * sits over longitude a - GMST
 */
export function celestialToWorld(gmst: number): matrix {
	return matrix.axisRotation(vec3.unitY, -gmst)
}

export function celestialToWorldVector(direction: vec3, gmst: number): vec3 {
	const c = Math.cos(gmst)
	const s = Math.sin(gmst)
	return new vec3(direction.x * c - direction.z * s, direction.y, direction.x * s + direction.z * c)
}

/**
 * Fraction of the moon's disc that is lit as seen from the Earth, from the
 * geocentric sun and moon directions (0 new, 1 full)
 */
export function illuminatedFraction(sunDirection: vec3, moonDirection: vec3): number {
	const cosElongation = sunDirection.x * moonDirection.x + sunDirection.y * moonDirection.y + sunDirection.z * moonDirection.z
	return (1 - cosElongation) / 2
}
